import React, { useCallback } from 'react'
import { AsyncSelect, Field } from '@workwhile/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  fetchAssignableUsers,
  userPermissionsKeys,
} from 'queries/admin/userPermissions'
import { AssignableUser } from 'api/admin/userPermissions/types'

type UserOption = {
  label: string
  value: number
  user: AssignableUser
}

type UserPickerProps = {
  value?: number | null
  selectedUser?: AssignableUser | null
  onChange: (user: AssignableUser | undefined) => void
  error?: string
}

export const UserPicker = ({
  value,
  selectedUser,
  onChange,
  error,
}: UserPickerProps) => {
  const queryClient = useQueryClient()

  const loadOptions = useCallback(
    async (rawInput: string) => {
      const keyword = rawInput.trim()
      if (!keyword) {
        return []
      }

      const users = await queryClient.fetchQuery({
        queryKey: userPermissionsKeys.assignableUsers(keyword),
        queryFn: () => fetchAssignableUsers(keyword),
      })

      return users.map(
        (user): UserOption => ({
          label: `${user.name} (${user.email})`,
          value: user.userId,
          user,
        })
      )
    },
    [queryClient]
  )

  const selectedOption: UserOption | null = selectedUser
    ? {
        label: `${selectedUser.name} (${selectedUser.email})`,
        value: selectedUser.userId,
        user: selectedUser,
      }
    : null

  return (
    <Field label="User" error={error}>
      <AsyncSelect
        block
        isClearable
        isSearchable
        placeholder="Search by name or email"
        loadOptions={loadOptions}
        cacheOptions
        menuPortalTarget={document.body}
        value={selectedOption}
        onChange={(option) => {
          onChange(option?.user)
        }}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 10011,
            pointerEvents: 'auto',
          }),
        }}
        noOptionsMessage={() =>
          value ? 'No matching users' : 'Type to search admin users'
        }
      />
    </Field>
  )
}
